/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { AxiosError } from 'axios';

import { APIService } from '@/src/infrastructure/Protocol/APIService';

import { IEmblueApiMethodNameMap, IInsightsService } from './IInsightsService';

import { IInsightsParamStart } from '@/modules/CampaignsModule/interfaces/Insights';
import { IGraphEvolutionMonths } from '@/modules/InsightsModule/interfaces/ContactsEvolution';
import {
  IActivityUser,
  InsightsContactsMetrics,
  InsightsFiltersActionsParam,
  InsightsMetricsActions,
} from '@/modules/InsightsModule/interfaces/ContactsSummary';

/* eslint-disable @typescript-eslint/no-explicit-any */
export class InsightsService implements IInsightsService {
  private api: APIService;
  private pathReports = '/api/v2.1/reports';

  private constructor(payload: IInsightsParamStart) {
    this.api = new APIService(payload.url_ema_app_v2, payload.jwt);
  }

  /**
   * @desc Catch errors from API
   */
  private catchErrors(error: AxiosError<Error>, method: string): void {
    const { message } = error;
    console.error(`❌ EM_Error InsightsService[${method}]:`, message);
  }

  /**
   * @desc Create new instance of InsightsService
   */
  static create(payload: IInsightsParamStart): IInsightsService {
    return new InsightsService(payload);
  }

  async getContactsMetrics(): Promise<InsightsContactsMetrics | null> {
    try {
      const { data } = await this.api.get<InsightsContactsMetrics>(
        `${this.pathReports}/contacts/status`
      );
      return data;
    } catch (error) {
      this.catchErrors(error as AxiosError<Error>, 'getContactsMetrics');
      return null;
    }
  }

  async getActionsMetrics(
    filters: InsightsFiltersActionsParam
  ): Promise<InsightsMetricsActions | null> {
    try {
      const url = new URL(`${this.api.baseURL}${this.pathReports}/campaigns/actions`);

      if (filters.startDate) url.searchParams.append('startDate', filters.startDate);
      if (filters.endDate) url.searchParams.append('endDate', filters.endDate);
      if (filters.filterType)
        url.searchParams.append('filterType', filters.filterType as unknown as string);

      const { data } = await this.api.get<InsightsMetricsActions>(url.toString());
      return data;
    } catch (error) {
      this.catchErrors(error as AxiosError<Error>, 'getActionsMetrics');
      return null;
    }
  }

  async getContactsGraphEvolution(
    startDate: string,
    endDate: string
  ): Promise<IGraphEvolutionMonths[] | null> {
    try {
      const { data } = await this.api.get<IGraphEvolutionMonths[]>(
        `${this.pathReports}/contacts/evolution`,
        {
          startDate,
          endDate,
        }
      );
      return data;
    } catch (error) {
      this.catchErrors(error as AxiosError<Error>, 'getContactsGraphEvolution');
      return null;
    }
  }

  async updateContactsStatus(accountIds: number[]): Promise<boolean> {
    try {
      await this.api.post(`${this.pathReports}/contacts/status`, {
        accountIds,
      });
      return true;
    } catch (error) {
      this.catchErrors(error as AxiosError<Error>, 'updateContactsStatus');
      return false;
    }
  }

  async getInsightsActivityUser(): Promise<IActivityUser[] | null> {
    try {
      const { data } = await this.api.get<IActivityUser[]>(`${this.pathReports}/contacts/activity`);
      return data;
    } catch (error) {
      this.catchErrors(error as AxiosError<Error>, 'getInsightsActivityUser');
      return null;
    }
  }

  async downloadActionReport(campaignActionId: number): Promise<{ data: ArrayBuffer } | null> {
    try {
      const response = await this.api.post<ArrayBuffer>(
        `${this.pathReports}/campaigns/${campaignActionId}/download`,
        undefined,
        true,
        {
          responseType: 'arraybuffer',
        }
      );
      return {
        data: response.data,
      };
    } catch (error) {
      console.error('Error downloading report:', error);
      this.catchErrors(error as AxiosError<Error>, 'downloadActionReport');
      return null;
    }
  }
}

export const InsightsServiceMethods: IEmblueApiMethodNameMap = Object.getOwnPropertyNames(
  InsightsService.prototype
).reduce((h: any, k: any) => {
  h[k] = k;
  return h;
}, {} as typeof InsightsService);
